import React from 'react';
import { Box, Button, Flex, useToast, useTheme, Text, BoxProps } from '@chakra-ui/react';
import { FiCopy } from 'react-icons/fi';

interface UrlCopyFieldProps extends BoxProps {
  url: string;
  label?: string;
  successMessage?: string;
  errorMessage?: string;
  showLabel?: boolean;
  copyButtonText?: string;
}

const UrlCopyField: React.FC<UrlCopyFieldProps> = ({
  url,
  label = 'URL:',
  successMessage = 'URL copied to clipboard',
  errorMessage = 'Failed to copy to clipboard',
  showLabel = true,
  copyButtonText = 'Copy',
  ...boxProps
}) => {
  const toast = useToast();
  const theme = useTheme();

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: 'Copied!',
        description: successMessage,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Copy failed',
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box width="100%" maxWidth="100%" {...boxProps}>
      {showLabel && (
        <Text fontSize="md" fontWeight="semibold" color={theme.colors.mainText} mb={3}>
          {label}
        </Text>
      )}

      <Flex
        direction={{ base: 'column', md: 'row' }}
        align={{ base: 'flex-start', md: 'stretch' }}
        gap="12px"
        width="100%"
        maxWidth="100%"
      >
        <Box
          p="12px 16px"
          fontSize="md"
          bg={`${theme.colors.mainText}11`}
          color={theme.colors.mainText}
          borderRadius={theme.border_radius.border_radius_1}
          flex="1"
          minWidth="0"
          width="100%"
          maxWidth="100%"
          minHeight="2.5rem"
          display="flex"
          alignItems="center"
          fontFamily="mono"
          wordBreak="break-all"
          overflowWrap="break-word"
        >
          {url}
        </Box>

        <Button
          leftIcon={<FiCopy />}
          onClick={copyToClipboard}
          colorScheme="primary"
          borderRadius={theme.border_radius.border_radius_1}
          flexShrink="0"
          display={{ base: 'none', md: 'flex' }}
          px={4}
          py={0}
          minH="2.5rem"
          h="100%"
        >
          {copyButtonText}
        </Button>
      </Flex>
    </Box>
  );
};

export default UrlCopyField;
